/* ==========================================================================
   ASPOOM · comportamiento de interfaz
   --------------------------------------------------------------------------
   · Cabecera: sólida al hacer scroll y, opcionalmente, se retira al bajar.
   · Panel de navegación móvil con foco atrapado.
   · Selección rápida de variante dentro de la tarjeta de producto.
   · Vídeos de fondo que sólo se reproducen cuando están en pantalla.
   · Ajuste del revelado de la intro con las animaciones de scroll de Dawn.

   Sin dependencias. Si algo falla, la tienda sigue siendo navegable.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------ */
  /* Cabecera                                                            */
  /* ------------------------------------------------------------------ */

  function initHeader() {
    var header = document.querySelector('[data-aspoom-header]');
    if (!header) return;

    var mode = header.dataset.sticky || 'on-scroll-up';
    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      var delta = y - lastY;

      header.classList.toggle('as-header--scrolled', y > 8);

      if (mode === 'on-scroll-up' && !isPanelOpen()) {
        if (y > 220 && delta > 4) {
          header.classList.add('as-header--hidden');
        } else if (delta < -4 || y <= 220) {
          header.classList.remove('as-header--hidden');
        }
      }

      lastY = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ------------------------------------------------------------------ */
  /* Paneles (navegación móvil)                                          */
  /* ------------------------------------------------------------------ */

  var openPanel = null;
  var lastTrigger = null;

  function isPanelOpen() {
    return openPanel !== null;
  }

  function focusables(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
      function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
  }

  function trapInPanel(event) {
    if (!openPanel || event.key !== 'Tab') return;
    var list = focusables(openPanel);
    if (!list.length) return;

    var first = list[0];
    var last = list[list.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openPanelByName(name, trigger) {
    var panel = document.querySelector('[data-aspoom-panel="' + name + '"]');
    var overlay = document.querySelector('[data-aspoom-overlay]');
    if (!panel) return;

    lastTrigger = trigger || null;
    openPanel = panel;

    panel.removeAttribute('hidden');
    if (overlay) overlay.removeAttribute('hidden');

    // Reflujo forzado en lugar de requestAnimationFrame: fija el estado
    // cerrado para que la transición arranque desde ahí, y funciona
    // aunque la pestaña no esté pintando (rAF puede no dispararse).
    void panel.offsetHeight;

    panel.classList.add('is-open');
    if (overlay) overlay.classList.add('is-open');

    document.documentElement.classList.add('as-no-scroll');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // Segundo reflujo: `focus()` no hace nada sobre un elemento que
    // todavía es `visibility: hidden`, así que hay que aplicar el estilo
    // antes de mover el foco.
    void panel.offsetHeight;

    var list = focusables(panel);
    if (list.length) list[0].focus();

    document.addEventListener('keydown', trapInPanel);
  }

  function closePanel() {
    if (!openPanel) return;

    var panel = openPanel;
    var overlay = document.querySelector('[data-aspoom-overlay]');

    panel.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');

    document.documentElement.classList.remove('as-no-scroll');
    document.removeEventListener('keydown', trapInPanel);

    var name = panel.dataset.aspoomPanel;
    var trigger = document.querySelector('[data-aspoom-open="' + name + '"]');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    var restore = lastTrigger || trigger;
    if (restore && typeof restore.focus === 'function') restore.focus();

    var hide = function () {
      panel.setAttribute('hidden', '');
      if (overlay && !document.querySelector('.as-panel.is-open')) overlay.setAttribute('hidden', '');
    };

    if (reduceMotion) {
      hide();
    } else {
      window.setTimeout(hide, 620);
    }

    openPanel = null;
    lastTrigger = null;
  }

  function initPanels() {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-aspoom-open]');
      if (opener) {
        event.preventDefault();
        openPanelByName(opener.dataset.aspoomOpen, opener);
        return;
      }

      if (event.target.closest('[data-aspoom-close]') || event.target.closest('[data-aspoom-overlay]')) {
        event.preventDefault();
        closePanel();
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && openPanel) closePanel();
    });

    // Al navegar desde el panel, no dejar el scroll bloqueado.
    document.addEventListener('click', function (event) {
      if (openPanel && event.target.closest('a[href]') && openPanel.contains(event.target)) {
        document.documentElement.classList.remove('as-no-scroll');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Tarjeta de producto: selección rápida de variante                   */
  /* ------------------------------------------------------------------ */

  function initCards() {
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-aspoom-option]');
      if (!button || button.disabled) return;

      var card = button.closest('[data-aspoom-card]');
      if (!card) return;

      event.preventDefault();
      selectOption(card, parseInt(button.dataset.aspoomOption, 10), button.dataset.aspoomValue);
    });
  }

  function cardVariants(card) {
    if (card._aspoomVariants) return card._aspoomVariants;
    var script = card.querySelector('[data-aspoom-variants]');
    if (!script) return null;
    try {
      card._aspoomVariants = JSON.parse(script.textContent);
    } catch (e) {
      card._aspoomVariants = null;
    }
    return card._aspoomVariants;
  }

  function currentSelection(card) {
    var selection = [];
    card.querySelectorAll('[data-aspoom-option][aria-pressed="true"]').forEach(function (el) {
      selection[parseInt(el.dataset.aspoomOption, 10) - 1] = el.dataset.aspoomValue;
    });
    return selection;
  }

  function selectOption(card, position, value) {
    // Estado visual del grupo tocado
    card.querySelectorAll('[data-aspoom-option="' + position + '"]').forEach(function (el) {
      el.setAttribute('aria-pressed', el.dataset.aspoomValue === value ? 'true' : 'false');
    });

    var variants = cardVariants(card);
    if (!variants) return;

    var selection = currentSelection(card);

    // La primera coincidencia con las opciones ya elegidas; si la
    // combinación no existe, se cae a la primera disponible de ese valor.
    var match =
      variants.find(function (v) {
        return selection.every(function (val, i) {
          return val === undefined || v.options[i] === val;
        });
      }) ||
      variants.find(function (v) {
        return v.options[position - 1] === value;
      });

    if (!match) return;

    applyVariant(card, match, position);
    refreshAvailability(card, variants, selection, position);
  }

  function applyVariant(card, variant, changedPosition) {
    var input = card.querySelector('[data-aspoom-variant-input]');
    if (input) {
      input.value = variant.id;
      input.disabled = !variant.available;
    }

    var submit = card.querySelector('[data-aspoom-submit]');
    if (submit) {
      submit.disabled = !variant.available;
      submit.removeAttribute('aria-disabled');
      var label = submit.querySelector('span');
      if (label) {
        label.textContent = variant.available
          ? submit.dataset.labelAdd || label.textContent
          : (window.variantStrings && window.variantStrings.soldOut) || label.textContent;
      }
    }

    var sold = card.querySelector('[data-aspoom-soldout]');
    if (sold) sold.hidden = variant.available;

    var price = card.querySelector('[data-aspoom-price]');
    if (price) {
      price.classList.toggle('as-card__price--sale', !!variant.compare);
      price.innerHTML = variant.compare
        ? '<del>' + variant.compare + '</del><ins>' + variant.price + '</ins>'
        : '<span>' + variant.price + '</span>';
    }

    // La imagen sólo se cambia si el color es lo que se ha tocado: cambiar
    // de talla no debería mover la foto.
    var colorPosition = parseInt(card.dataset.colorPosition, 10);
    if (variant.image && changedPosition === colorPosition) {
      var main = card.querySelector('.as-card__img--main');
      if (main && main.tagName === 'IMG' && main.src !== variant.image) {
        main.removeAttribute('srcset');
        main.src = variant.image;
      }
    }
  }

  function refreshAvailability(card, variants, selection, changedPosition) {
    var colorPosition = parseInt(card.dataset.colorPosition, 10);
    var sizePosition = parseInt(card.dataset.sizePosition, 10);
    if (!sizePosition || changedPosition !== colorPosition) return;

    var color = selection[colorPosition - 1];

    card.querySelectorAll('[data-aspoom-option="' + sizePosition + '"]').forEach(function (el) {
      var possible = variants.filter(function (v) {
        return (
          v.options[sizePosition - 1] === el.dataset.aspoomValue &&
          (!color || v.options[colorPosition - 1] === color)
        );
      });
      el.disabled = !possible.some(function (v) {
        return v.available;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Vídeos de fondo                                                     */
  /* ------------------------------------------------------------------ */

  function initBackgroundVideos() {
    var videos = document.querySelectorAll('[data-aspoom-bgvideo]');
    if (!videos.length || !('IntersectionObserver' in window)) return;

    if (reduceMotion) {
      videos.forEach(function (video) {
        video.removeAttribute('autoplay');
        video.pause();
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            if (video.preload === 'none') video.preload = 'auto';
            var promise = video.play();
            if (promise && promise.catch) promise.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    videos.forEach(function (video) {
      video.muted = true;
      video.playsInline = true;
      observer.observe(video);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Encaje con la intro                                                 */
  /* ------------------------------------------------------------------ */

  function initIntroBridge() {
    // Mientras la tienda está fuera de pantalla, el IntersectionObserver de
    // Dawn marca todo como «offscreen». Al empezar a subir, lo que va a
    // quedar en la primera pantalla debe llegar ya compuesto: si no, el
    // usuario ve la tienda subir vacía y el contenido aparecer después.
    document.addEventListener('aspoom:intro:reveal', function () {
      var site = document.getElementById('site-content');
      if (!site) return;

      var origin = site.getBoundingClientRect().top;
      var limit = window.innerHeight * 1.15;

      site.querySelectorAll('.scroll-trigger').forEach(function (el) {
        if (el.getBoundingClientRect().top - origin < limit) {
          el.classList.remove('scroll-trigger--offscreen');
          el.classList.add('scroll-trigger--cancel');
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */

  function boot() {
    initHeader();
    initPanels();
    initCards();
    initBackgroundVideos();
    initIntroBridge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // El editor de temas reinyecta secciones sin recargar la página.
  document.addEventListener('shopify:section:load', function () {
    initHeader();
    initBackgroundVideos();
  });
})();
